import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Flex,
  Modal,
  Input,
  Form,
  Row,
  Col,
  Typography,
  notification,
  Button,
  Upload,
  UploadFile,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import styles from "pages/seniorMaster/finance/finance.module.scss";
import { UploadChangeParam } from "antd/es/upload/interface";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { useParams, useNavigate } from "react-router-dom";
import {
  MasterInvoice,
  MasterAccrual,
  MasterListItem,
} from "pages/seniorMaster/finance/types";
import useMasterInvoiceStore from "stores/seniorMaster/masterInvoice";
import clsx from "clsx";

const { Text } = Typography;
const { TextArea } = Input;

type ComponentProps = {};

const { Title } = Typography;

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;

    return false;
  }, [subRoute]);

  const {
    list,
    setList,
    isPostInProcess,
    setIsPostInProcess,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
    isFileUploading,
    setIsFileUploading,
    isChangeStatusInProcess,
    setIsChangeStatusInProcess,
    isConfirmInProcess,
    setIsConfirmInProcess,
    isTableHidden,
    setIsTableHidden,
  } = useMasterInvoiceStore((store) => store);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isAddMode, form, values, setIsError, setIsSubmittable]);

  //  Get all knowledge base items
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/master_invoices`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const invoices: MasterInvoice[] = data.records.map(
            (invoice: MasterInvoice) => ({
              ...invoice,
              type: "invoice",
            })
          );

          const accruals: MasterAccrual[] = data.accruals.map(
            (accrual: MasterAccrual) => ({
              ...accrual,
              type: "accrual",
            })
          );

          const records = [...invoices, ...accruals]
            .sort((a, b) => {
              const dateA =
                a.type === "invoice"
                  ? new Date(a.created_at).getTime()
                  : new Date(a.date).getTime();
              const dateB =
                b.type === "invoice"
                  ? new Date(b.created_at).getTime()
                  : new Date(b.date).getTime();
              return dateA - dateB;
            })
            .filter((item) => item.type === "invoice");

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list]
  );

  //  When started, get all data
  useEffect(() => {
    getList();
  }, [getList]);

  const handleCancelButton = useCallback(
    (masterInvoice?: MasterInvoice) => {
      if (!masterInvoice?.id) return;

      let cancelComment = "";

      Modal.confirm({
        title: "Вы уверены, что хотите отменить этот счет?",
        content: (
          <div className={styles.modalContent}>
            <div className={styles.warningText}>
              Это действие нельзя будет отменить.
            </div>
            <Input.TextArea
              className={styles.commentInput}
              placeholder="Введите комментарий"
              onChange={(e) => (cancelComment = e.target.value)}
            />
          </div>
        ),
        okText: "Да, отменить",
        cancelText: "Отмена",
        onOk: async () => {
          if (!cancelComment.trim()) {
            Modal.error({
              title: "Ошибка",
              content: "Введите комментарий перед отменой.",
            });
            return Promise.reject();
          }

          setIsChangeStatusInProcess((prev) => ({
            ...prev,
            [masterInvoice.id]: true,
          }));

          const fetchData = async () => {
            const url = `${apiDomain}/api/records/master_invoices/${masterInvoice.id}`;
            const options: RequestInit = {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                status: "canceled",
                cancel_comment: cancelComment,
              }),
              credentials: "include",
            };

            try {
              const response = await fetch(url, options);
              if (!response.ok) throw new Error();

              await getList(true);
            } catch (error) {
              openNotificationWithError({
                message: "Ошибка при изменении статуса",
              });
            } finally {
              setIsChangeStatusInProcess((prev) => ({
                ...prev,
                [masterInvoice.id]: false,
              }));
            }
          };

          return fetchData();
        },
      });
    },
    [getList, openNotificationWithError, setIsChangeStatusInProcess]
  );

  const handleShowTable = useCallback(() => {
    setIsTableHidden((isTableHidden) => {
      return !isTableHidden;
    });
  }, [setIsTableHidden]);

  const handleConfirmButton = useCallback(
    (masterInvoice?: MasterInvoice) => {
      if (!masterInvoice?.id) return;

      setIsConfirmInProcess((prev) => ({
        ...prev,
        [masterInvoice.id]: true,
      }));

      const fetchData = async () => {
        const url = `${apiDomain}/api/records/master_invoices/${masterInvoice.id}`;
        const options: RequestInit = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "confirmed",
          }),
          credentials: "include",
        };

        try {
          const response = await fetch(url, options);
          if (!response.ok) throw new Error();

          await getList(true);
        } catch (error) {
          openNotificationWithError({
            message: "Ошибка при изменении статуса",
          });
        } finally {
          setIsConfirmInProcess((prev) => ({
            ...prev,
            [masterInvoice.id]: false,
          }));
        }
      };

      fetchData();
    },
    [getList, openNotificationWithError, setIsConfirmInProcess]
  );

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/finance/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/finance`);
  }, [navigate]);

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    return false;
  }, [isAddMode, isPostInProcess]);

  //  Amount changed
  useEffect(() => {
    const allValues = form.getFieldsValue();

    const amount = !allValues?.amount
      ? ""
      : (allValues?.amount as string)
          .replace(/[^\d.,]/g, "")
          .replace(/,/g, ".")
          .replace(/^(\d*(\.\d{0,2})?).*$/, "$1");

    form.setFieldsValue({
      amount: amount,
    });
  }, [form, values?.amount]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;

    const fetchData = async () => {
      setIsError(false);

      if (isAddMode) setIsPostInProcess(true);

      const url = `${apiDomain}/api/records/master_invoices`;

      const options: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: values?.amount ? values.amount : null,
          invoice_link: values?.invoiceLink ? values.invoiceLink : "",
          comment: values?.comment ? values.comment : "",
          user_file_id: values.file?.[0].response?.[0]?.id
            ? values.file[0].response[0].id
            : values.file?.[0]?.uid && !isNaN(Number(values.file[0].uid))
            ? Number(values.file[0].uid)
            : null,
        }),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        getList(true);

        form.resetFields();
        form
          .validateFields({ validateOnly: true })
          .then(() => setIsSubmittable(true))
          .catch(() => setIsSubmittable(false));

        navigate(`/finance`);

        if (isAddMode) setIsPostInProcess(false);
      } catch (error) {
        setIsError(true);

        if (isAddMode) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isAddMode,
    form,
    values,
    isInProcess,
    navigate,
    setIsError,
    getList,
    setIsPostInProcess,
    setIsSubmittable,
  ]);

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === "uploading");
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(async (previewFile: UploadFile) => {
    console.log(previewFile);
  }, []);

  //  Set columns for table
  const columnsUnconfirmed: ColumnsType = useMemo(() => {
    const statusMap: Record<string, string> = {
      unconfirmed: "Добавлен",
      confirmed: "Подтвержден",
      canceled: "Отменен",
      paid: "Оплачен",
    };

    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          return record.id;
        },
      },
      {
        title: "Дата",
        key: "created_at",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedDate = record.created_at
              ? new Date(record.created_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          const formattedDate = record.date
            ? new Date(record.date).toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : null;

          return formattedDate;
        },
      },
      {
        title: "Мастер",
        key: "master",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return record.master?.name;
          }

          return null;
        },
      },
      {
        title: "Сумма",
        key: "paid",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedAmount = parseFloat(record.amount)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

            return formattedAmount;
          }

          return null;
        },
      },
      {
        title: "Файл",
        key: "file",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record?.user_file?.file_path) return null;

            return (
              <a
                href={`/userFiles/${record.user_file.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.user_file.file_name}
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Ссылка",
        key: "link",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.invoice_link) return null;

            return (
              <a
                href={record.invoice_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ссылка
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.comment) return null;

            return record.comment;
          }

          return null;
        },
      },
      {
        title: "Статус",
        key: "status",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return statusMap[record.status] ?? null;
          }

          return null;
        },
      },
      {
        key: "confirm",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;
          const recordStyles = record.styles;

          if (record.type === "invoice") {
            if (record.status !== "unconfirmed") return null;

            return (
              <Button
                onClick={() => handleConfirmButton(record)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
                loading={!!isConfirmInProcess?.[record?.id]}
              >
                Подтвердить
              </Button>
            );
          }

          return null;
        },
      },
      {
        key: "cancel",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;
          const recordStyles = record.styles;

          if (record.type === "invoice") {
            if (record.status !== "unconfirmed") return null;

            return (
              <Button
                onClick={() => handleCancelButton(record)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
                loading={!!isChangeStatusInProcess?.[record?.id]}
              >
                Отменить
              </Button>
            );
          }

          return null;
        },
      },
    ];

    return result;
  }, [
    isInProcess,
    isChangeStatusInProcess,
    isConfirmInProcess,
    handleCancelButton,
    handleConfirmButton,
  ]);

  //  Set columns for table
  const columnsAllButUnconfirmed: ColumnsType = useMemo(() => {
    const statusMap: Record<string, string> = {
      unconfirmed: "Добавлен",
      confirmed: "Подтвержден",
      canceled: "Отменен",
      paid: "Оплачен",
    };

    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          return record.id;
        },
      },
      {
        title: "Дата",
        key: "created_at",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedDate = record.created_at
              ? new Date(record.created_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          const formattedDate = record.date
            ? new Date(record.date).toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : null;

          return formattedDate;
        },
      },
      {
        title: "Мастер",
        key: "master",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return record.master?.name;
          }

          return null;
        },
      },
      {
        title: "Сумма",
        key: "paid",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedAmount = parseFloat(record.amount)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

            return formattedAmount;
          }

          return null;
        },
      },
      {
        title: "Файл",
        key: "file",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record?.user_file?.file_path) return null;

            return (
              <a
                href={`/userFiles/${record.user_file.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.user_file.file_name}
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Ссылка",
        key: "link",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.invoice_link) return null;

            return (
              <a
                href={record.invoice_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ссылка
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.comment) return null;

            return record.comment;
          }

          return null;
        },
      },
      {
        title: "Статус",
        key: "status",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return statusMap[record.status] ?? null;
          }

          return null;
        },
      },
      {
        title: "Дата подтверждения",
        key: "confirmedAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["confirmed", "paid"].includes(record.status)) return null;

            const formattedDate = record.confirmed_at
              ? new Date(record.confirmed_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
      {
        title: "Дата оплаты",
        key: "paidAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["paid"].includes(record.status)) return null;

            const formattedDate = record.paid_at
              ? new Date(record.paid_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
      {
        title: "Дата закрытия",
        key: "canceledAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["canceled"].includes(record.status)) return null;

            const formattedDate = record.canceled_at
              ? new Date(record.canceled_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
    ];

    return result;
  }, []);

  const tableDataUnconfirmed = useMemo(() => {
    return list.data.filter(
      (item) => item.type === "invoice" && item.status === "unconfirmed"
    );
  }, [list.data]);

  const tableDataAllButUnconfirmed = useMemo(() => {
    return list.data.filter(
      (item) => item.type === "invoice" && item.status !== "unconfirmed"
    );
  }, [list.data]);

  return (
    <MobileInnerWrapper>
      <Row justify="space-between" align="middle" className={styles.titleBlock}>
        <Col>
          <Title level={3} className={styles.title}>
            {isAddMode ? `Добавление счета` : `Счета`}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={isAddMode ? handleBackButton : handleAddButton}
            disabled={isInProcess}
          >
            {isAddMode ? `Вернуться к списку` : `Добавить счет`}
          </Button>
        </Col>
      </Row>
      {!!isAddMode && (
        <>
          <Flex vertical gap={16}>
            <Form
              layout={"vertical"}
              form={form}
              onFinish={handleFinish}
              initialValues={{
                amount: "",
                invoiceLink: "",
                comment: "",
                userFileId: null,
              }}
            >
              <Form.Item
                name="amount"
                label="Сумма"
                validateTrigger={false}
                required={true}
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста, введите сумму!",
                  },
                ]}
              >
                <Input
                  size="large"
                  name="amount"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item
                label="Прикрепить файл"
                name="file"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  action={`${apiDomain}/api/upload-file`}
                  withCredentials={true}
                  multiple={true}
                  onChange={handleUploadFileChange}
                  onPreview={handlePreview}
                  listType="picture"
                >
                  <Button
                    icon={<UploadOutlined />}
                    disabled={isFileUploading || !list.isLoaded}
                  >
                    {isFileUploading ? `Идет загрузка` : `Загрузить файл`}
                  </Button>
                </Upload>
              </Form.Item>
              <Form.Item
                name="invoiceLink"
                label="Ссылка на счет"
                validateTrigger={false}
                required={false}
              >
                <Input
                  size="large"
                  name="invoiceLink"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item
                name="comment"
                label="Комментарий"
                validateTrigger={false}
                required={false}
              >
                <TextArea
                  size="large"
                  name="comment"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  className={styles.button}
                  disabled={
                    !isSubmittable || !!isFileUploading || !list.isLoaded
                  }
                  htmlType="submit"
                  loading={isInProcess}
                >
                  Сохранить
                </Button>
              </Form.Item>
              {!!isError && (
                <Form.Item>
                  <Text type="danger">Не удалось сохранить счёт</Text>
                </Form.Item>
              )}
            </Form>
          </Flex>
        </>
      )}
      {!isAddMode && (
        <>
          <Title level={5}>Неподтвержденные</Title>
          <Table
            dataSource={tableDataUnconfirmed}
            columns={columnsUnconfirmed}
            rowKey="id"
            pagination={false}
            loading={!list.isLoaded}
            styles={styles}
          />
          <div className={styles.additionalTable}>
            <Row
              justify="space-between"
              align="middle"
              className={styles.titleBlock}
            >
              <Col>
                <Title level={5}>Все счета</Title>
              </Col>
              <Col>
                <Button type="primary" onClick={handleShowTable}>
                  {isTableHidden ? `Показать` : `Скрыть`}
                </Button>
              </Col>
            </Row>
            <Table
              dataSource={tableDataAllButUnconfirmed}
              columns={columnsAllButUnconfirmed}
              rowKey="id"
              pagination={false}
              loading={!list.isLoaded}
              className={clsx(!!isTableHidden && styles.isHidden)}
              styles={styles}
            />
          </div>
        </>
      )}
    </MobileInnerWrapper>
  );
};

export default Page;
